/**
 * Auto-logout service for users with orders after 2 hours of inactivity
 */
class AutoLogoutService {
  constructor() {
    this.inactivityTimer = null;
    this.warningTimer = null;
    this.isActive = false;
    this.lastActivityTime = Date.now();
    this.showingWarning = false;
    
    // 2 hours in milliseconds
    this.INACTIVITY_TIMEOUT = 72 * 60 * 60 * 1000; // 2 hours
    // Show warning 10 minutes before logout
    this.WARNING_TIMEOUT = this.INACTIVITY_TIMEOUT - (10 * 60 * 1000); // 1h 50min
    
    // Events to track user activity
    this.activityEvents = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];
    
    this.store = null;
    this.router = null;
  }

  /**
   * Initialize the auto-logout service
   */
  init(store, router) {
    this.store = store;
    this.router = router;
    this.setupActivityListeners();
    this.checkUserStatus();
  }

  /**
   * Check if user has orders and should be monitored for auto-logout
   */
  checkUserStatus() {
    if (!this.store || !this.router) return;

    const progressItems = this.store.getters.progressItems;
    const isAuthenticated = this.store.getters.isAuthenticated;
    const currentRoute = this.router.currentRoute.path;
    
    // Only monitor users who are authenticated and have orders
    const hasOrders = progressItems && Array.isArray(progressItems) && progressItems.length > 0;
    
    // Only activate on specific pages: /home and /medical-form (and its variants)
    const isTargetPage = currentRoute === '/home' || currentRoute.startsWith('/medical-form');
    
    if (isAuthenticated && hasOrders && isTargetPage) {
      this.startMonitoring();
    } else {
      this.stopMonitoring();
    }
  }

  /**
   * Start monitoring user activity
   */
  startMonitoring() {
    if (this.isActive) return;
    
    console.log('Auto-logout: Starting 2-hour inactivity monitoring for user with orders on /home or /medical-form pages');
    this.isActive = true;
    this.lastActivityTime = Date.now();
    this.resetTimers();
  }

  /**
   * Stop monitoring user activity
   */
  stopMonitoring() {
    if (!this.isActive) return;
    
    console.log('Auto-logout: Stopping inactivity monitoring');
    this.isActive = false;
    this.clearTimers();
    this.hideWarning();
  }

  /**
   * Setup event listeners for user activity
   */
  setupActivityListeners() {
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.handleUserActivity.bind(this), true);
    });
  }

  /**
   * Handle user activity events
   */
  handleUserActivity() {
    if (!this.isActive) return;
    
    this.lastActivityTime = Date.now();
    this.resetTimers();
    
    // Hide warning if user becomes active
    if (this.showingWarning) {
      this.hideWarning();
    }
  }

  /**
   * Reset the inactivity timers
   */
  resetTimers() {
    this.clearTimers();
    
    // Set warning timer (1h 50min)
    this.warningTimer = setTimeout(() => {
      this.showLogoutWarning();
    }, this.WARNING_TIMEOUT);
    
    // Set logout timer (2h)
    this.inactivityTimer = setTimeout(() => {
      this.performAutoLogout();
    }, this.INACTIVITY_TIMEOUT);
  }

  /**
   * Clear all timers
   */
  clearTimers() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * Show logout warning to user
   */
  showLogoutWarning() {
    if (this.showingWarning) return;
    
    this.showingWarning = true;
    
    // Create warning modal
    const warningModal = document.createElement('div');
    warningModal.id = 'auto-logout-warning';
    warningModal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 10000;
      display: flex;
      justify-content: center;
      align-items: center;
    `;
    
    const warningContent = document.createElement('div');
    warningContent.style.cssText = `
      background: white;
      padding: 30px;
      border-radius: 10px;
      text-align: center;
      max-width: 400px;
      margin: 20px;
    `;
    
    warningContent.innerHTML = `
      <h3 style="margin-top: 0; color: #333;">Session Timeout Warning</h3>
      <p style="color: #666; margin-bottom: 20px;">
        You will be automatically logged out in 10 minutes due to 2 hours of inactivity. 
        Click "Stay Logged In" to continue your session.
      </p>
      <button id="stay-logged-in" style="
        background: #e9ff86;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 5px;
        cursor: pointer;
        margin-right: 10px;
      ">Stay Logged In</button>
      <button id="logout-now" style="
        background: #80888d;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 5px;
        cursor: pointer;
      ">Logout Now</button>
    `;
    
    warningModal.appendChild(warningContent);
    document.body.appendChild(warningModal);
    
    // Handle button clicks
    document.getElementById('stay-logged-in').onclick = () => {
      this.hideWarning();
      this.handleUserActivity(); // Reset timers
    };
    
    document.getElementById('logout-now').onclick = () => {
      this.performAutoLogout();
    };
  }

  /**
   * Hide the logout warning
   */
  hideWarning() {
    if (!this.showingWarning) return;
    
    const warningModal = document.getElementById('auto-logout-warning');
    if (warningModal) {
      warningModal.remove();
    }
    
    this.showingWarning = false;
  }

  /**
   * Perform automatic logout
   */
  performAutoLogout() {
    if (!this.store) return;
    
    console.log('Auto-logout: Performing automatic logout due to 2-hour inactivity');
    
    this.hideWarning();
    this.stopMonitoring();
    
    // Show logout message
    alert('You have been automatically logged out due to 2 hours of inactivity.');
    
    // Clear stored auth token
    localStorage.removeItem('_ymain_key_');
    
    // Call logout API if available
    try {
      const baseUrl = process.env.VUE_APP_API_ENDPOINT;
      if (baseUrl && this.store.getters.authToken) {
        fetch(`${baseUrl}/api/logout`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${this.store.getters.authToken}`
          }
        }).catch(err => console.log('Logout API call failed:', err));
      }
    } catch (error) {
      console.log('Error calling logout API:', error);
    }
    
    // Reload the page to clear all state
    window.location.reload();
  }

  /**
   * Manually trigger user status check (call when orders are loaded)
   */
  recheckUserStatus() {
    this.checkUserStatus();
  }

  /**
   * Cleanup method
   */
  destroy() {
    this.stopMonitoring();
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.handleUserActivity.bind(this), true);
    });
  }
}

// Create singleton instance
const autoLogoutService = new AutoLogoutService();

export default autoLogoutService;